import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import type { RecipeSummary } from './useRecipes';

interface UseRecipeDetailReturn {
  recipe: RecipeSummary | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useRecipeDetail(
  recipeId: string | undefined,
  admin = false,
): UseRecipeDetailReturn {
  const [recipe, setRecipe] = useState<RecipeSummary | null>(null);
  const [loading, setLoading] = useState(!!recipeId);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!recipeId) return;
    try {
      setLoading(true);
      setError(null);
      const path = admin
        ? `/arches/crafting/admin/recipes/${recipeId}`
        : `/arches/crafting/recipes/${recipeId}`;
      const { data } = await api.get<{ recipe: RecipeSummary }>(path);
      setRecipe(data.recipe);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recipe');
    } finally {
      setLoading(false);
    }
  }, [recipeId, admin]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { recipe, loading, error, refresh: fetch };
}
